import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Switch,
    IconButton,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { AiOutlineClose } from 'react-icons/ai';
import { AccessTime } from '@mui/icons-material';
import axios from '../../../axiosConfig';

const TimeSettingPopup = ({
    open,
    onClose,
    itemId,
    itemType,
    currentAvailability = [],
    onSaveSuccess,
    clientId,
    itemName = ''
}) => {
    const [loading, setLoading] = useState(false);
    const [availability, setAvailability] = useState([]);

    // Generate hours in 24-hour format (00:00 to 23:30 with 30 min intervals)
    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute of ['00', '30']) {
                const formattedHour = hour.toString().padStart(2, '0');
                times.push(`${formattedHour}:${minute}`);
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    const daysOfWeek = [
        { id: 'monday', label: 'Monday' },
        { id: 'tuesday', label: 'Tuesday' },
        { id: 'wednesday', label: 'Wednesday' },
        { id: 'thursday', label: 'Thursday' },
        { id: 'friday', label: 'Friday' },
        { id: 'saturday', label: 'Saturday' },
        { id: 'sunday', label: 'Sunday' }
    ];

    // Initialize availability data
    useEffect(() => {
        if (open) {
            const initialAvailability = daysOfWeek.map(day => {
                const existingDay = currentAvailability?.find(item => item.day === day.id);
                return {
                    day: day.id,
                    dayLabel: day.label,
                    from: existingDay?.from || '08:00',
                    to: existingDay?.to || '19:00',
                    active: !!existingDay
                };
            });
            setAvailability(initialAvailability);
        }
    }, [open, JSON.stringify(currentAvailability)]);

    const handleTimeChange = (index, field, value) => {
        const newAvailability = [...availability];
        newAvailability[index][field] = value;
        setAvailability(newAvailability);
    };

    const handleActiveChange = (index, checked) => {
        const newAvailability = [...availability];
        newAvailability[index].active = checked;

        // Only reset times if activating and times are not valid
        if (checked && (!timeOptions.includes(newAvailability[index].from) || !timeOptions.includes(newAvailability[index].to))) {
            newAvailability[index].from = '08:00';
            newAvailability[index].to = '19:00';
        }
        setAvailability(newAvailability);
    };

    const handleSave = async () => {
        try {
            setLoading(true);

            // Filter only active days and validate times
            const activeAvailability = availability
                .filter(day => day.active)
                .map(({ day, from, to }) => {
                    // Ensure times are in valid format
                    const validFrom = timeOptions.includes(from) ? from : '08:00';
                    const validTo = timeOptions.includes(to) ? to : '19:00';
                    return { day, from: validFrom, to: validTo };
                });

            const payload = {
                client_id: clientId || 1,
                item_id: itemId,
                item_type: itemType,
                available_time: activeAvailability
            };

            // Check if availability already exists for this item
            const checkResponse = await axios.get('/partners/item_availability', {
                params: {
                    client_id: payload.client_id,
                    item_id: itemId,
                    item_type: itemType
                }
            });

            let response;
            if (checkResponse.data.data && checkResponse.data.data.length > 0) {
                // Update existing
                const existingId = checkResponse.data.data[0].id;
                response = await axios.put('/partners/item_availability', {
                    id: existingId,
                    ...payload
                });
            } else {
                // Create new
                response = await axios.post('/partners/item_availability', payload);
            }

            if (response.data.success) {
                if (onSaveSuccess) {
                    onSaveSuccess(response.data.data);
                }
                onClose();
            }
        } catch (error) {
            console.error('Failed to save availability:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxWidth: '550px'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                borderBottom: '1px solid #e0e0e0'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AccessTime color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        Set Availability Schedule
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    <AiOutlineClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {itemName && (
                    <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary' }}>
                        Setting schedule for: <strong>{itemName}</strong>
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    {/* Header */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Day
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                From
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                To
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Active
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Days List */}
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                        {availability.map((schedule, index) => (
                            <Grid
                                container
                                spacing={2}
                                alignItems="center"
                                key={schedule.day}
                                sx={{
                                    py: 1.5,
                                    borderBottom: index < availability.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    backgroundColor: schedule.active ? 'transparent' : 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                {/* Day */}
                                <Grid item xs={3}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color={schedule.active ? 'text.primary' : 'text.disabled'}
                                    >
                                        {schedule.dayLabel}
                                    </Typography>
                                </Grid>

                                {/* From Time Dropdown */}
                                <Grid item xs={3}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={!schedule.active}
                                    >
                                        <Select
                                            value={schedule.from}
                                            onChange={(e) => handleTimeChange(index, 'from', e.target.value)}
                                            displayEmpty
                                            sx={{
                                                height: '40px',
                                                backgroundColor: schedule.active ? 'white' : '#f5f5f5',
                                                '& .MuiSelect-select': {
                                                    padding: '8px 32px 8px 12px'
                                                }
                                            }}
                                        >
                                            {timeOptions.map((time) => (
                                                <MenuItem key={`from-${time}`} value={time}>
                                                    {time}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* To Time Dropdown */}
                                <Grid item xs={3}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={!schedule.active}
                                    >
                                        <Select
                                            value={schedule.to}
                                            onChange={(e) => handleTimeChange(index, 'to', e.target.value)}
                                            displayEmpty
                                            sx={{
                                                height: '40px',
                                                backgroundColor: schedule.active ? 'white' : '#f5f5f5',
                                                '& .MuiSelect-select': {
                                                    padding: '8px 32px 8px 12px'
                                                }
                                            }}
                                        >
                                            {timeOptions.map((time) => (
                                                <MenuItem key={`to-${time}`} value={time}>
                                                    {time}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Active Switch */}
                                <Grid item xs={3}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Switch
                                            checked={schedule.active}
                                            onChange={(e) => handleActiveChange(index, e.target.checked)}
                                            color="primary"
                                            size="medium"
                                            sx={{
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: schedule.active ? 'primary.main' : 'grey.400'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </Box>

            </DialogContent>

            <DialogActions sx={{
                p: 3,
                borderTop: '1px solid #e0e0e0',
                gap: 2
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        minWidth: '100px',
                        borderRadius: '6px',
                        textTransform: 'none'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{
                        minWidth: '120px',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    {loading ? 'Saving...' : 'Save Schedule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TimeSettingPopup;